import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useGetAllRoleList, useGetUserMenusForRole, useDeleteBulkRoleMenus } from "../../../hooks";
import { Loading } from "../../atoms";
import { Table } from "../../molecules";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { IRoleMenu } from "../../../hooks/role-menu/useGetRoleMenuList";

export const RoleMenuBulkDeleteTab: React.FC = () => {
  const [sourceRoleId, setSourceRoleId] = useState<number | "">("");
  const [roleMenus, setRoleMenus] = useState<IRoleMenu[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { data: rawRoleList, isLoading: rolesLoading } = useGetAllRoleList("ACTIVE");
  const { mutate: getUserMenus, isLoading: getUserMenusLoading } = useGetUserMenusForRole();
  const { mutate: deleteBulkRoleMenus, isLoading: deleteLoading } = useDeleteBulkRoleMenus();
  const setSnackbar = useSetSnackBar();

  const roleList = useMemo(() => {
    return Array.isArray(rawRoleList?.data)
      ? rawRoleList.data.map((role: { name: string; id: number }) => ({
          label: role.name,
          value: role.id,
        }))
      : [];
  }, [rawRoleList?.data]);

  const handleSourceRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setSourceRoleId(roleId);
    setSelectedMenuIds([]);
    
    if (roleId) {
      getUserMenus(
        { roleId },
        {
          onSuccess: (data) => {
            if (data.isSuccess) {
              setRoleMenus(data.data || []);
            } else {
              setRoleMenus([]);
              setSnackbar({
                severity: "error",
                description: data.message || "Rol menüleri alınamadı",
                isOpen: true,
              });
            }
          },
          onError: () => {
            setRoleMenus([]);
            setSnackbar({
              severity: "error",
              description: "Rol menüleri alınırken bir hata oluştu",
              isOpen: true,
            });
          },
        }
      );
    } else {
      setRoleMenus([]);
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "roleName", headerName: "Rol Adı", width: 200 },
      { field: "menuName", headerName: "Menü Adı", width: 200 },
      { field: "create", headerName: "Oluştur", width: 100, type: 'boolean' },
      { field: "read", headerName: "Oku", width: 100, type: 'boolean' },
      { field: "update", headerName: "Güncelle", width: 100, type: 'boolean' },
      { field: "delete", headerName: "Sil", width: 100, type: 'boolean' },
    ];
  }, []);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleDelete = () => {
    if (selectedMenuIds.length === 0) {
      setSnackbar({
        severity: "warning",
        description: "Lütfen silinecek menüleri seçiniz",
        isOpen: true,
      });
      return;
    }

    const ids = selectedMenuIds.map(id => Number(id));
    
    deleteBulkRoleMenus(
      {
        ids: ids,
      },
      {
        onSuccess: (deleteData) => {
          if (deleteData.isSuccess) {
            setSnackbar({
              severity: "success",
              description: deleteData.message || "Seçilen menüler başarıyla silindi",
              isOpen: true,
            });
            setSelectedMenuIds([]);
            // Refetch the list
            if (sourceRoleId) {
              handleSourceRoleChange({ target: { value: sourceRoleId } } as SelectChangeEvent<number>);
            }
          } else {
            setSnackbar({
              severity: "error",
              description: deleteData.message || "Silme işlemi başarısız oldu",
              isOpen: true,
            });
          }
        },
        onError: (error) => {
          setSnackbar({
            severity: "error",
            description: "Silme işlemi sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const isLoading = rolesLoading || getUserMenusLoading || deleteLoading;
  const selectedRole = roleList.find((role: { label: string; value: number }) => role.value === sourceRoleId);

  return (
    <Box p={3}>
      {isLoading && <Loading />}
      
      <Typography variant="h6" gutterBottom>
        Rol Menü Seçerek Toplu Silme
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        İstediğiniz menüleri seçerek silebilirsiniz.
      </Typography>

      <Stack spacing={3}>
        <Stack direction="row" spacing={3} maxWidth={800}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="source-role-select-label">Rol</InputLabel>
            <Select
              labelId="source-role-select-label"
              value={sourceRoleId}
              label="Rol"
              onChange={handleSourceRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {sourceRoleId && roleMenus.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedRole?.label} Rolü Menüleri ({roleMenus.length} adet)
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip 
                  label={`${selectedMenuIds.length} seçildi`} 
                  color={selectedMenuIds.length > 0 ? "primary" : "default"}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={selectedMenuIds.length === 0 || isLoading}
                  size="small"
                >
                  Seçilenleri Sil ({selectedMenuIds.length})
                </Button>
              </Stack>
            </Box>

            <Table
              columnVisibilityConfigKey="RoleMenuBulkDeleteListingColumnVisibilityKey"
              paginationModel={paginationModel}
              onPaginationModelChange={handleChangePagination}
              paginationMode="client"
              rowCount={roleMenus.length}
              sx={{ width: isDesktop ? 1308 : window.innerWidth - 50, height: 600 }}
              checkboxSelection
              onRowSelectionModelChange={setSelectedMenuIds}
              rowSelectionModel={selectedMenuIds}
              rows={roleMenus}
              columns={columns}
            />
          </>
        )}

        {sourceRoleId && roleMenus.length === 0 && !getUserMenusLoading &&(
          <Typography variant="body2" color="text.secondary">
            Seçilen rolde menü bulunmamaktadır.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
